import * as Sentry from "@sentry/browser";
import type { Breadcrumb, ErrorEvent, StackFrame } from "@sentry/browser";

import { getAppName, getAppVersion } from "../api/appInfo";
import { error as logError } from "../logger/logger";
import { isLocalEnvironment } from "../runtime/Environment";
import { generalizeEventMessages, getEventMessage, snapshotEventMessages } from "./eventMessage";
import { isReportedFailure, trackFailure } from "./failures";
import { isExternalError, isIgnoredMessage, isSpamError, stackFramesToText } from "./filters";
import {
	APP_START_LOADER_SOURCE,
	type AppStartLoaderError,
	adoptAppStartLoader,
} from "./loader/contract";
import { TEST_PROBE_TYPE, TestProbeError } from "./probes";
import { createRateLimiter } from "./rateLimit";
import { makeCloudTransport } from "./transport";
import {
	DEFAULT_DSN,
	DEFAULT_ENDPOINT,
	ENVIRONMENT,
	ERROR,
	ERROR_IMPACT,
	type ErrorTrackingConfig,
	type ErrorTrackingUser,
	HOST_ENVIRONMENTS,
	type Level,
	PRODUCTION_ENVIRONMENTS,
	SENTRY_LEVEL,
	TAG,
	TEST_CRASH_EVENT,
	TEST_ERROR_SOURCE,
	TEST_REQUEST_PATH,
	TEST_ROUTE_CRASH_EVENT,
	TRACKER_SENTRY,
	WARN,
} from "./types";
import { generalizeUrl, redactCredentialsInText } from "./urlGeneralize";

export { isReportedFailure, runWithFailureReporting, trackFailure } from "./failures";
export { TestProbeError } from "./probes";
export {
	DEFAULT_DSN,
	DEFAULT_ENDPOINT,
	ENVIRONMENT,
	ERROR,
	ERROR_IMPACT,
	TAG,
	TEST_CRASH_EVENT,
	TEST_CRASH_PARAM,
	TEST_ROUTE_CRASH_EVENT,
	WARN,
} from "./types";
export type {
	Environment,
	ErrorTrackingConfig,
	ErrorTrackingUser,
	ErrorTransportOptions,
	Level,
	FailureContext,
} from "./types";

const resolveEnvironment = (override?: string): string => {
	if (override) return override;
	const host = typeof window !== "undefined" ? window.location.host : "";
	if (isLocalEnvironment()) return ENVIRONMENT.TEST;
	const match = HOST_ENVIRONMENTS.find(([fragment]) => host.indexOf(fragment) >= 0);
	return match ? match[1] : ENVIRONMENT.PROD;
};

export const isProductionEnvironment = (environment = resolveEnvironment()) =>
	PRODUCTION_ENVIRONMENTS.indexOf(environment) >= 0;

// Cap breadcrumb messages so oversized log lines don't dominate the payload.
const MAX_BREADCRUMB_MESSAGE = 500;
const MAX_PRINTED_DETAIL = 2000;

const generalizeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
	if (breadcrumb.data) {
		// `message` already carries the flattened log line; drop the duplicated raw data.
		delete breadcrumb.data.arguments;
		delete breadcrumb.data.logger;
		if (typeof breadcrumb.data.url === "string") {
			// These URLs later become error titles; retain the API version for grouping.
			breadcrumb.data = {
				...breadcrumb.data,
				url: generalizeUrl(breadcrumb.data.url, ["version"]),
			};
		}
		for (const key of ["from", "to"] as const) {
			if (typeof breadcrumb.data[key] === "string") {
				breadcrumb.data = { ...breadcrumb.data, [key]: generalizeUrl(breadcrumb.data[key]) };
			}
		}
	}
	if (typeof breadcrumb.message === "string") {
		breadcrumb.message = redactCredentialsInText(breadcrumb.message);
		if (breadcrumb.message.length > MAX_BREADCRUMB_MESSAGE) {
			breadcrumb.message = breadcrumb.message.slice(0, MAX_BREADCRUMB_MESSAGE - 1) + "…";
		}
	}
	return breadcrumb;
};

const generalizeFrames = (frames: StackFrame[] | undefined) => {
	frames?.forEach(frame => {
		if (frame.filename) frame.filename = generalizeUrl(frame.filename);
	});
};

const generalizeEventUrls = (event: ErrorEvent) => {
	if (event.request?.url) event.request.url = generalizeUrl(event.request.url);
	const headers = event.request?.headers;
	if (headers) {
		for (const name of Object.keys(headers)) {
			if (name.toLowerCase() === "referer" && headers[name])
				headers[name] = generalizeUrl(headers[name]);
		}
	}
	event.exception?.values?.forEach(value => generalizeFrames(value.stacktrace?.frames));
};

// Failed-request messages carry no URL, so every failing call collapses into one
// title. Append the endpoint recovered from the matching fetch/xhr breadcrumb.
const REQUEST_FAILED_PREFIX = "Request failed with status code";
const NETWORK_ERROR_SUFFIX = "Network Error";

// Network errors surface as status 0 in the fetch/xhr breadcrumbs.
const resolveFailedRequestStatus = (message: string): number | null => {
	if (message.indexOf(REQUEST_FAILED_PREFIX) === 0) {
		const statusCode = Number(message.split(" ")[5]);
		return Number.isFinite(statusCode) ? statusCode : null;
	}
	if (message.endsWith(NETWORK_ERROR_SUFFIX)) return 0;
	return null;
};

const appendRequestUrlToMessage = (event: ErrorEvent) => {
	const exception = event.exception?.values?.[0];
	const message = getEventMessage(event);
	if (!message || / for (?:https?:\/\/|\/)\S*$/.test(message)) return;

	const statusCode = resolveFailedRequestStatus(message);
	if (statusCode === null) return;

	const breadcrumbs = event.breadcrumbs;
	if (!breadcrumbs?.length) return;

	// The failing request is the newest breadcrumb with a matching status code.
	for (let i = breadcrumbs.length - 1; i > -1; i--) {
		const data = breadcrumbs[i].data;
		const url = data?.url;
		if (Number(data?.status_code) === statusCode && typeof url === "string") {
			const updated = `${message} for ${generalizeUrl(url, ["version"])}`;
			if (exception) {
				exception.value = updated;
			} else {
				event.message = updated;
			}
			return;
		}
	}
};

const CRASH_QUOTA = 2;
const FLOW_FAILURE_QUOTA = 5;

let installed = false;
let rateLimiter: ReturnType<typeof createRateLimiter> | null = null;

const MAX_LOADER_HANDOVER = 5;

const reportLoaderErrors = (errors: AppStartLoaderError[]) => {
	errors.slice(0, MAX_LOADER_HANDOVER).forEach(entry => {
		const error = entry.error instanceof Error ? entry.error : new Error(entry.message);
		trackError(error, ERROR, {
			[TAG.SOURCE]: APP_START_LOADER_SOURCE,
			[TAG.OPERATION]: entry.kind,
		});
	});
};

export const initErrorTracking = (config: ErrorTrackingConfig = {}) => {
	if (installed) return;
	installed = true;

	const loader = adoptAppStartLoader();
	// Tracking is a non-critical concern; a failing init must never break app bootstrap.
	try {
		init(config);
		if (loader && !loader.sent) reportLoaderErrors(loader.errors);
	} catch (initError) {
		logError("Error tracking initialization failed", initError);
	}
};

const init = (config: ErrorTrackingConfig) => {
	const environment = resolveEnvironment(config.environment);
	// Resolved per event/send so `setAppName` calls made after init are picked up;
	// an explicit `config.appName` stays fixed.
	const resolveAppName = () => config.appName ?? getAppName();
	const appVersion = config.appVersion ?? getAppVersion();
	// Report in every deployed environment; local dev has no backend to receive it.
	const enabled = !isLocalEnvironment();
	rateLimiter = createRateLimiter();
	const crashRateLimiter = createRateLimiter(CRASH_QUOTA);
	const flowFailureRateLimiter = createRateLimiter(FLOW_FAILURE_QUOTA);

	const cloudTransport = makeCloudTransport({
		endpoint: config.endpoint ?? DEFAULT_ENDPOINT,
		appName: resolveAppName,
		appVersion,
		getAuthHeaders: config.getAuthHeaders,
	});
	// Service workers can be suspended between an error and its send, so the offline
	// transport persists envelopes to IndexedDB and retries them on resume.
	const transport = config.offline
		? Sentry.makeBrowserOfflineTransport(cloudTransport)
		: cloudTransport;

	Sentry.init({
		dsn: config.dsn ?? DEFAULT_DSN,
		environment,
		release: config.release ?? appVersion,
		attachStacktrace: true,
		// The SDK disables itself (silently, in prod builds) when it thinks it runs inside
		// a browser extension embedded in a foreign page. Consumers of this tracker are
		// genuine web apps, so that detection could only ever false-positive; opt out.
		skipBrowserExtensionCheck: true,
		// Don't beacon SDK telemetry envelopes; only real errors should reach the endpoint.
		sendClientReports: false,
		// Metrics are on by default in v10; nothing consumes them and the transport only
		// forwards error events anyway.
		enableMetrics: false,
		// Session tracking is off: drop the default BrowserSession integration (the v9+
		// replacement for `autoSessionTracking: false`). Console output alone does not
		// imply an unhandled exception or a crashed application.
		integrations: defaults => [
			...defaults.filter(integration => integration.name !== "BrowserSession"),
			Sentry.captureConsoleIntegration({ levels: ["error"], handled: true }),
		],
		transport,
		transportOptions: config.transportOptions,
		// App-name tags are stamped per event in `beforeSend`, not here, so they
		// reflect `setAppName` calls made after init.
		initialScope: {
			tags: {
				[TAG.TRACKER]: TRACKER_SENTRY,
			},
		},
		beforeBreadcrumb: (breadcrumb: Breadcrumb) => {
			const generalized = generalizeBreadcrumb(breadcrumb);
			return config.beforeBreadcrumb ? config.beforeBreadcrumb(generalized) : generalized;
		},
		beforeSend: async (event: ErrorEvent, hint) => {
			// Let synchronous error boundaries replace React's automatic/console capture.
			await Promise.resolve();
			if (isReportedFailure(hint.originalException)) return null;
			const isCrash = event.tags?.[TAG.IMPACT] === ERROR_IMPACT.APP_CRASH;
			const isFlowFailure = event.tags?.[TAG.IMPACT] === ERROR_IMPACT.FLOW_FAILURE;
			const isFailure = isCrash || isFlowFailure;
			const exception = event.exception?.values?.[0];
			const message = getEventMessage(event) ?? "";
			const detail = event.contexts?.failure?.detail;
			const printed =
				typeof detail === "string" ? `${message}\n${detail.slice(0, MAX_PRINTED_DETAIL)}` : message;

			// eslint-disable-next-line no-console
			console.log("%c<ERROR>\n" + printed, "color:red");

			if (!enabled) {
				return null;
			}

			const frames = exception?.stacktrace?.frames;
			const stackText = stackFramesToText(frames);

			if (
				!isFailure &&
				isSpamError(message, stackText, config.spamStackFragments, config.spamMessages)
			)
				return null;

			// Network failures while the browser is offline carry no signal.
			if (
				!isFailure &&
				typeof navigator !== "undefined" &&
				navigator.onLine === false &&
				message.indexOf(NETWORK_ERROR_SUFFIX) > -1
			) {
				return null;
			}

			// Classify before URL generalization: it strips scheme/origin from frame
			// filenames, which origin-based classifiers rely on.
			const isExternal = isExternalError(frames, config.isInternalFrame, config.isVendorFrame);
			if (!isFailure && isExternal && config.dropExternal) return null;
			const isVendor = !!config.isVendorFrame && !!frames?.some(config.isVendorFrame);

			// Consume quota only for events that passed the drop filters, so filtered
			// floods can't starve real errors.
			let eventRateLimiter = rateLimiter;
			if (isCrash) eventRateLimiter = crashRateLimiter;
			else if (isFlowFailure) eventRateLimiter = flowFailureRateLimiter;
			if (eventRateLimiter && !eventRateLimiter.consume()) return null;

			generalizeEventUrls(event);
			appendRequestUrlToMessage(event);
			// Keep endpoint context in the original before message rules remove identifiers.
			const originalMessages = snapshotEventMessages(event);
			generalizeEventMessages(event, originalMessages);

			const appName = resolveAppName();
			event.tags = {
				...event.tags,
				// Shortcut and probe errors keep their real impact and source; the tag lets reports exclude them.
				...(exception?.type === TEST_PROBE_TYPE ? { [TAG.PROBE]: "true" } : {}),
				[TAG.IMPACT]: event.tags?.[TAG.IMPACT] ?? ERROR_IMPACT.UNKNOWN,
				[TAG.SOURCE]: event.tags?.[TAG.SOURCE] ?? exception?.mechanism?.type ?? "manual",
				[TAG.APP_NAME]: appName,
				[TAG.CODE_SOURCE]: event.tags?.[TAG.CODE_SOURCE] || appName,
			};

			if (typeof window !== "undefined") {
				// Generalized to stay within Sentry's tag value length limit.
				event.tags = { ...event.tags, [TAG.PAGE_URL]: generalizeUrl(window.location.href) };
			}

			let downgrade = isIgnoredMessage(message, config.ignoreMessages);
			if (isExternal) {
				event.tags = { ...event.tags, [TAG.IS_EXTERNAL]: true };
				downgrade = true;
			}
			if (isVendor) {
				event.tags = { ...event.tags, [TAG.IS_VENDOR]: true };
			}

			if (isFailure) {
				event.level = isCrash ? "fatal" : "error";
				event.tags = { ...event.tags, [TAG.LEVEL]: ERROR };
			} else if (downgrade) {
				event.level = SENTRY_LEVEL[WARN];
				event.tags = { ...event.tags, [TAG.LEVEL]: WARN };
			}

			return config.beforeSend ? config.beforeSend(event) : event;
		},
	});

	registerTestErrorShortcut({ production: isProductionEnvironment(environment) });
};

export const setUser = (user: ErrorTrackingUser) => {
	Sentry.setUser({ id: String(user.id) });
	if (user.instanceName !== undefined) Sentry.setTag(TAG.INSTANCE_NAME, user.instanceName);
	const sessionId = user.sessionId ?? `${user.id}_${Date.now()}`;
	Sentry.setTag(TAG.SESSION_ID, sessionId);
};

// Clears the user and user-derived tags so later errors aren't misattributed.
export const clearUser = () => {
	Sentry.setUser(null);
	Sentry.setTag(TAG.INSTANCE_NAME, "");
	Sentry.setTag(TAG.SESSION_ID, "");
};

export const setErrorContext = (key: string, value: unknown) => {
	Sentry.setTag(key, value == null ? "" : String(value));
};

export const addBreadcrumb = (breadcrumb: Breadcrumb) => {
	Sentry.addBreadcrumb(breadcrumb);
};

// Optional `tags` are applied only to this event (scoped), unlike the
// persistent tags set through `setErrorContext`. Values are limited to scalars:
// tags are indexed by Sentry, so error details belong on the captured error itself.
export const trackError = (
	error: string | Error,
	level: Level = ERROR,
	tags?: Record<string, string | number | boolean>
) => {
	const sentryLevel = SENTRY_LEVEL[level];
	Sentry.withScope(scope => {
		scope.setTag(TAG.LEVEL, level);
		scope.setLevel(sentryLevel);
		Object.entries(tags ?? {}).forEach(([key, value]) => {
			scope.setTag(key, value);
		});
		if (typeof error === "string") {
			Sentry.captureMessage(error, sentryLevel);
		} else {
			Sentry.captureException(error);
		}
	});
};

// ctrl+alt+shift + key exercises the reporting pipeline on demand; see docs/error-impact.md.
const TEST_ERROR_ACTIONS: Record<string, () => void> = {
	KeyE: () => trackError(new TestProbeError("DummyError!"), WARN),
	KeyN: () =>
		trackError(new TestProbeError("Test non-blocking error"), ERROR, {
			[TAG.IMPACT]: ERROR_IMPACT.NON_BLOCKING,
			[TAG.SOURCE]: TEST_ERROR_SOURCE,
		}),
	KeyF: () =>
		trackFailure(new TestProbeError("Test flow failure"), {
			impact: ERROR_IMPACT.FLOW_FAILURE,
			source: TEST_ERROR_SOURCE,
		}),
	KeyU: () => {
		setTimeout(() => {
			throw new TestProbeError("Test uncaught exception");
		});
	},
	KeyR: () => {
		void Promise.reject(new TestProbeError("Test unhandled rejection"));
	},
	KeyL: () => {
		// eslint-disable-next-line no-console
		console.error(new TestProbeError("Test console error"));
	},
	KeyQ: () => {
		void fetch(TEST_REQUEST_PATH).then(
			response => {
				if (!response.ok) {
					trackError(new TestProbeError(`Request failed with status code ${response.status}`));
				}
			},
			() => trackError(new TestProbeError("Network Error"))
		);
	},
	KeyV: () => {
		document.dispatchEvent(new CustomEvent(TEST_ROUTE_CRASH_EVENT));
	},
	KeyC: () => {
		document.dispatchEvent(new CustomEvent(TEST_CRASH_EVENT));
	},
};

const CRASH_TEST_KEYS: ReadonlySet<string> = new Set(["KeyC", "KeyV", "KeyU", "KeyR"]);

const isEditableTarget = (target: EventTarget | null) =>
	target instanceof HTMLElement &&
	(target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

let shortcutRegistered = false;
let crashShortcutsEnabled = true;

export const registerTestErrorShortcut = ({ production = false } = {}) => {
	crashShortcutsEnabled = !production;
	if (typeof document === "undefined" || shortcutRegistered) return;
	shortcutRegistered = true;
	document.addEventListener(
		"keydown",
		event => {
			if (event.repeat || !event.ctrlKey || !event.altKey || !event.shiftKey) return;
			if (event.getModifierState("AltGraph") || isEditableTarget(event.target)) return;
			// Physical key: Alt turns letters into dead keys on macOS.
			if (CRASH_TEST_KEYS.has(event.code) && !crashShortcutsEnabled) return;
			TEST_ERROR_ACTIONS[event.code]?.();
		},
		true
	);
};
